import { useState } from 'react';
import { detailOr } from '@/services/api';
import { publicEventsApi } from '@/features/publicsite/events/api/publicEventsApi';
import { feedbackPayload, participationAfterFeedback } from '@/features/publicsite/events/mappers';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import type { EventParticipation } from '@/features/publicsite/types/events/EventParticipation';

/**
 * The post-event evaluation's answers, and their submission. Held above the form rather than in
 * it, so what a participant typed survives the form being hidden and shown again.
 */
export function useEventFeedbackForm() {
  const { t } = useTranslation(publicEvents);
  const [rating, setRating] = useState(5);
  const [comments, setComments] = useState('');
  const [presentationRating, setPresentationRating] = useState(5);
  const [futureNeeds, setFutureNeeds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  /** Sends the evaluation; `onAccepted` gets what the participant's standing now reads. */
  const submit = async (
    eventId: string,
    onAccepted: (participation: EventParticipation) => void,
  ) => {
    const values = { rating, comments, presentationRating, futureNeeds };
    setSubmitting(true);
    setError('');
    try {
      await publicEventsApi.leaveFeedback(eventId, feedbackPayload(values));
      onAccepted(participationAfterFeedback(values));
    } catch (err) {
      setError(detailOr(err, t('feedbackError')));
    } finally {
      setSubmitting(false);
    }
  };

  return {
    rating,
    comments,
    presentationRating,
    futureNeeds,
    submitting,
    error,
    setRating,
    setComments,
    setPresentationRating,
    setFutureNeeds,
    submit,
  };
}
